import { useRef, useState, useMemo } from 'react';
import { DataGridPro, GridColumnVisibilityModel, GridPreferencePanelsValue, GridToolbarContainer, useGridApiRef } from '@mui/x-data-grid-pro';
import { Box, IconButton, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { Stack, useTheme } from '@mui/system';
import { useSelector } from 'react-redux';
import { formatCurrency } from 'src/utils/format-currency';
import { useLocales } from 'src/locales';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import Iconify from '../iconify';
import countries from '../../utils/countries.json';
import { formatNumber } from 'src/utils/format-number';
import { brandedCampaignsVisibility } from 'src/utils/branded-campaigns';
import { palette } from 'src/sections/overview/dashboards/views/non-branded/figma-palette';

const getCountryCode = (name: any) => countries.find((c) => c.name.toLowerCase() === name.toLowerCase())?.code;

// Figma palette (node 3059:8495)
const COMPARE_POSITIVE = '#0db46b';
const COMPARE_NEGATIVE = '#e03535';
const COMPARE_NEUTRAL = '#637381';
const VALUE_COLOR = '#333C47';

// Per-metric accent colors used for the inline progress bar (match the chart palette).
const BAR_COLORS = {
    blended_ctr: '#5119b7',
    brand_traffic: '#880e4f',
    savings: '#118D57',
    ad_cost_with_cb: '#DAA335',
    revenue_or_conversions: '#0052B4',
};

// Signed, integer-rounded percentage change, clamped to ±999% (Figma node 3059:8495).
export const formatComparePct = (current: any, compare: any) => {
    const base = compare ?? 0;
    const diff = (current ?? 0) - base;
    if (base === 0) return diff > 0 ? '>999%' : diff < 0 ? '<-999%' : '0%';
    // Divide by |base| so the sign always follows the direction of the change
    // (savings can be negative) and stays consistent with compareColorPct.
    const pct = (diff / Math.abs(base)) * 100;
    if (pct > 999) return '>999%';
    if (pct < -999) return '<-999%';
    const rounded = Math.round(pct);
    return rounded > 0 ? `+${rounded}%` : `${rounded}%`;
};

// Absolute, signed delta (current − compare) shown when the %evol toggle is OFF.
// Percentage metrics (e.g. Blended CTR) read in percentage points (pp); currency and
// count metrics reuse the same compact formatters as their cell value.
export type CompareKind = 'percent' | 'currency' | 'number';
export const formatCompareDiff = (
    current: any,
    compare: any,
    kind: CompareKind,
    { currency = 'USD', locale = 'en' }: { currency?: string; locale?: string } = {},
) => {
    const diff = (current ?? 0) - (compare ?? 0);
    const sign = diff > 0 ? '+' : diff < 0 ? '-' : '';
    const abs = Math.abs(diff);
    if (kind === 'percent') return `${sign}${abs.toFixed(1)}pp`;
    if (kind === 'currency') return `${sign}${formatCurrency(abs, currency, locale, true, 1)}`;
    return `${sign}${formatNumber(abs)}`;
};

// Green when the change is "good", red when "bad"; `invertedColor` flips it for cost-like metrics.
export const compareColorPct = (current: any, compare: any, invertedColor = false) => {
    const diff = (current ?? 0) - (compare ?? 0);
    if (diff === 0) return COMPARE_NEUTRAL;
    const isGood = diff > 0 ? !invertedColor : invertedColor;
    return isGood ? COMPARE_POSITIVE : COMPARE_NEGATIVE;
};

// Brand Traffic composition bar: organic (green) + paid (orange), from the shared figma palette.
// Brand traffic IS the captured traffic (organic + paid clicks); there is no "uncaptured" segment.
export const BRAND_TRAFFIC_COLORS = {
    organic: palette.organic,
    paid: palette.paid.shopping,
};

// 3-way split (Explore & Overview): Organic / Paid Search / Paid Shopping (Figma Signal 2026 Q2).
export const BRAND_TRAFFIC_3WAY_COLORS = {
    organic: '#34AD6A',
    paidSearch: '#FFC01F',
    paidShopping: '#FF9A1F',
};

// Builds the 3-way Organic / Paid Search / Paid Shopping (shopping + pmax) segments from a row.
// Accepts both the raw ad_clicks_from_* names and the *_brand_traffic aliases (traffic table.sql);
// shopping/pmax aliases are -1 when is_shopping=FALSE, hence the Math.max clamp. Returns null when
// the 3-way fields are absent or all zero so callers can fall back to the legacy 2-way split.
// `visibility` (workspace branded shopping/pmax campaign lists) drops the corresponding
// contribution — and the whole Paid Shopping segment when neither source is configured.
export const brandTraffic3WaySegments = (
    row: any,
    { showShopping = true, showPmax = true }: { showShopping?: boolean; showPmax?: boolean } = {}
) => {
    const organic = row.organic_clicks ?? row.organic_brand_traffic;
    const paidSearch = row.ad_clicks_from_search ?? row.search_brand_traffic;
    const shopping = showShopping ? row.ad_clicks_from_shopping ?? row.shopping_brand_traffic ?? 0 : 0;
    const pmax = showPmax ? row.ad_clicks_from_pmax ?? row.pmax_brand_traffic ?? 0 : 0;
    if (paidSearch == null && row.ad_clicks_from_shopping == null && row.ad_clicks_from_pmax == null
        && row.shopping_brand_traffic == null && row.pmax_brand_traffic == null) return null;
    const paidShopping = Math.max(0, shopping) + Math.max(0, pmax);
    const total = Math.max(0, organic ?? 0) + Math.max(0, paidSearch ?? 0) + paidShopping;
    if (total <= 0) return null;
    // Le segment groupe shopping + pmax ; quand seul PMax est configuré, l'étiquette
    // « Paid Shopping Traffic » serait trompeuse (workspace sans Shopping).
    const paidShoppingLabel = showShopping ? 'Paid Shopping Traffic' : 'Paid PMax Traffic';
    return [
        { label: 'Organic Traffic', value: Math.max(0, organic ?? 0), color: BRAND_TRAFFIC_3WAY_COLORS.organic },
        { label: 'Paid Search Traffic', value: Math.max(0, paidSearch ?? 0), color: BRAND_TRAFFIC_3WAY_COLORS.paidSearch },
        ...(showShopping || showPmax
            ? [{ label: paidShoppingLabel, value: paidShopping, color: BRAND_TRAFFIC_3WAY_COLORS.paidShopping }]
            : []),
    ];
};

// 100%-stacked composition of a brand-traffic row (organic + paid). Returns each segment's
// share as a percentage of the bar (summing to 100), or all zeros when there is no traffic.
export const brandTrafficSegments = (organic: any, paid: any) => {
    const o = Math.max(0, organic ?? 0);
    const p = Math.max(0, paid ?? 0);
    const total = o + p;
    if (total <= 0) return { organic: 0, paid: 0 };
    return {
        organic: (o / total) * 100,
        paid: (p / total) * 100,
    };
};

const capitalizeFirst = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s);

const leftHeader = (label: string) => () => (
    <span
        style={{
            fontFamily: 'Public Sans, Helvetica',
            fontWeight: 600,
            fontSize: 14,
            lineHeight: '16.8px',
            color: COMPARE_NEUTRAL,
            textAlign: 'left',
        }}
    >
        {label}
    </span>
);

// Shared metric cell: [value] · [proportional bar] · [signed % vs. compare].
export const renderMetricCell = ({
    displayValue,
    barValue,
    max,
    barColor,
    current,
    compare,
    invertedColor,
    showCompare,
    evolutionMode = true,
    compareKind = 'number',
    currency,
    locale,
    valueWidth = 70,
    valueAlign = 'right',
}: any) => {
    const pct = max > 0 ? Math.min(100, (barValue / max) * 100) : 0;
    return (
        <Stack direction="row" alignItems="center" sx={{ gap: '8px', width: 1, height: 1 }}>
            <Typography
                sx={{
                    // Left-aligned values overflow toward the bar (whiteSpace: nowrap), so a wide
                    // number must grow the slot (minWidth) rather than spill onto the bar. Right-
                    // aligned values overflow away from the bar, so a fixed width stays safe.
                    ...(valueAlign === 'left' ? { minWidth: valueWidth } : { width: valueWidth }),
                    flexShrink: 0,
                    textAlign: valueAlign,
                    fontSize: '14px',
                    fontWeight: 500,
                    lineHeight: '22px',
                    color: VALUE_COLOR,
                    whiteSpace: 'nowrap',
                    fontVariantNumeric: 'tabular-nums',
                }}
            >
                {displayValue}
            </Typography>
            <Box sx={{ flex: 1, minWidth: 8, overflow: 'hidden', borderRadius: '6px', display: 'flex', alignItems: 'center' }}>
                <Box
                    sx={{
                        height: 6,
                        borderRadius: '8px',
                        bgcolor: barColor,
                        width: `${pct}%`,
                        minWidth: barValue > 0 ? 4 : 0,
                    }}
                />
            </Box>
            {showCompare && (
                <Typography
                    sx={{
                        width: 56,
                        flexShrink: 0,
                        textAlign: 'right',
                        fontSize: '12px',
                        fontWeight: 400,
                        lineHeight: 1.2,
                        whiteSpace: 'nowrap',
                        fontVariantNumeric: 'tabular-nums',
                        color: compareColorPct(current, compare, invertedColor),
                    }}
                >
                    {evolutionMode
                        ? formatComparePct(current, compare)
                        : formatCompareDiff(current, compare, compareKind, { currency, locale })}
                </Typography>
            )}
        </Stack>
    );
};

// Brand Traffic cell: [value] · [100%-stacked organic/paid bar] · [signed % vs. compare].
export const renderBrandTrafficCell = ({ displayValue, organic, paid, current, compare, showCompare, evolutionMode = true, t, label, valueAlign = 'right', segments }: any) => {
    // `segments` (opt-in) : découpage N-way [{label, value, color}]. Par défaut : 2-way organic/paid
    // (comportement historique, identique — Business Overview). Explore passe un découpage 3-way.
    const parts: Array<{ label: string; value: number; color: string }> = segments ?? [
        { label: t('dashboard.brandTraffic.organicBrandTraffic'), value: organic, color: BRAND_TRAFFIC_COLORS.organic },
        { label: t('dashboard.brandTraffic.paidBrandTraffic'), value: paid, color: BRAND_TRAFFIC_COLORS.paid },
    ];
    const total = parts.reduce((sum, p) => sum + Math.max(0, p.value ?? 0), 0);
    const hasData = total > 0;
    const pctOf = (v: number) => (total > 0 ? (Math.max(0, v ?? 0) / total) * 100 : 0);
    const tooltip = (
        <Stack sx={{ gap: 1, py: 0.75, minWidth: 220 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ gap: 2.5, pb: 0.25 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                    {t('dashboard.brandTraffic.totalBrandTraffic')}
                </Typography>
                {label != null && label !== '' && (
                    <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{label}</Typography>
                )}
            </Stack>
            {parts.map((s) => (
                <Stack key={s.label} direction="row" alignItems="center" justifyContent="space-between" sx={{ gap: 2.5 }}>
                    <Stack direction="row" alignItems="center" sx={{ gap: 1 }}>
                        <Box sx={{ width: 3, height: 14, borderRadius: '2px', bgcolor: s.color, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: 13 }}>{s.label}</Typography>
                    </Stack>
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{formatNumber(s.value ?? 0)}</Typography>
                </Stack>
            ))}
        </Stack>
    );
    return (
        <Stack direction="row" alignItems="center" sx={{ gap: '8px', width: 1, height: 1 }}>
            <Typography
                sx={{
                    width: 70,
                    flexShrink: 0,
                    textAlign: valueAlign,
                    fontSize: '14px',
                    fontWeight: 500,
                    lineHeight: '22px',
                    color: VALUE_COLOR,
                    whiteSpace: 'nowrap',
                    fontVariantNumeric: 'tabular-nums',
                }}
            >
                {displayValue}
            </Typography>
            <Tooltip title={tooltip} arrow placement="top">
                <Box
                    sx={{
                        flex: 1,
                        minWidth: 8,
                        height: 6,
                        borderRadius: '6px',
                        overflow: 'hidden',
                        display: 'flex',
                        bgcolor: hasData ? 'transparent' : 'rgba(145, 158, 171, 0.2)',
                    }}
                >
                    {hasData &&
                        parts.map((s) => (
                            <Box key={s.label} sx={{ width: `${pctOf(s.value)}%`, bgcolor: s.color }} />
                        ))}
                </Box>
            </Tooltip>
            {showCompare && (
                <Typography
                    sx={{
                        width: 56,
                        flexShrink: 0,
                        textAlign: 'right',
                        fontSize: '12px',
                        fontWeight: 400,
                        lineHeight: 1.2,
                        whiteSpace: 'nowrap',
                        fontVariantNumeric: 'tabular-nums',
                        color: compareColorPct(current, compare, false),
                    }}
                >
                    {evolutionMode
                        ? formatComparePct(current, compare)
                        : formatCompareDiff(current, compare, 'number')}
                </Typography>
            )}
        </Stack>
    );
};

export const getColumns = ({ t, currentLang, workspace, theme, filterStore, split, isCompare, evolutionMode = true, currency, locale, maxValues, sinceCrossBrand }: any) => {
    // Per-row comparison only applies to grouped splits; date rows have no compare counterpart.
    const showCompare = isCompare && split !== 'date';
    const revenueLabel = t('dashboard.global.total_search_conversions');
    const isLead = workspace?.template === 'Lead';
    const brandedVisibility = brandedCampaignsVisibility(workspace);

    return [
        {
            field: 'split',
            headerName: split.charAt(0).toUpperCase() + split.slice(1).toLowerCase(),
            minWidth: 200,
            renderCell: ({ value }: any) => {
                const lowerSplit = split.toLowerCase();
                if (lowerSplit === 'platform') {
                    const display = capitalizeFirst(value);
                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {display.toLowerCase() === 'google' && <Iconify icon="devicon:google" sx={{ width: 16, height: 16 }} />}
                            {display.toLowerCase() === 'bing' && <Iconify icon="logos:bing" sx={{ width: 16, height: 16 }} />}
                            {display}
                        </Box>
                    );
                }
                if (lowerSplit === 'device') {
                    // Only capitalize first letter
                    const normalized = capitalizeFirst(value);
                    const icons: any = {
                        Mobile: 'icon-park:iphone',
                        Desktop: 'icon-park:i-mac',
                        Tablet: 'icon-park:ipad',
                    };
                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {icons[normalized] && <Iconify icon={icons[normalized]} sx={{ width: 16, height: 16 }} />}
                            {normalized}
                        </Box>
                    );
                }
                if (lowerSplit === 'country') {
                    const code = getCountryCode(value);
                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {code ? (
                                <img
                                    src={`https://flagcdn.com/w20/${code}.png`}
                                    alt={value}
                                    style={{ width: 20, height: 15, borderRadius: 2 }}
                                />
                            ) : (
                                <img
                                    style={{ width: 14, height: 14, marginRight: 6 }}
                                    src="/assets/location.png"
                                    alt="flag"
                                />
                            )}
                            {value}
                        </Box>
                    );
                }
                return value;
            },
        },
        {
            field: 'blended_ctr',
            headerName: 'Blended CTR',
            minWidth: isCompare ? 200 : 150,
            flex: 1,
            headerAlign: 'left',
            renderHeader: leftHeader('Blended CTR'),
            renderCell: (params: any) => {
                const value = params.value ?? 0;
                return renderMetricCell({
                    displayValue: `${value.toFixed(1)}%`,
                    barValue: value,
                    max: maxValues.blended_ctr,
                    barColor: BAR_COLORS.blended_ctr,
                    current: params.row.blended_ctr,
                    compare: params.row.blended_ctr_compare,
                    invertedColor: false,
                    showCompare,
                    evolutionMode,
                    compareKind: 'percent',
                    currency,
                    locale,
                    valueWidth: 50,
                });
            },
            type: 'number',
        },
        {
            field: 'brand_traffic',
            headerName: t('dashboard.brandTraffic.totalBrandTraffic'),
            minWidth: isCompare ? 230 : 180,
            flex: 1,
            headerAlign: 'left',
            renderHeader: leftHeader(t('dashboard.brandTraffic.totalBrandTraffic')),
            renderCell: (params: any) =>
                renderBrandTrafficCell({
                    displayValue: formatNumber(params.value ?? 0),
                    organic: params.row.organic_brand_traffic,
                    paid: params.row.paid_brand_traffic,
                    current: params.row.brand_traffic,
                    compare: params.row.brand_traffic_compare,
                    showCompare,
                    evolutionMode,
                    t,
                    label: params.row.split,
                    // 3-way Organic / Paid Search / Paid Shopping (design); falls back to the
                    // legacy 2-way organic/paid split when the API omits the ad_clicks_from_* fields.
                    segments: brandTraffic3WaySegments(params.row, brandedVisibility) ?? undefined,
                }),
            type: 'number',
        },
        {
            field: 'savings',
            headerName: t('dashboard.filters.savings'),
            minWidth: isCompare ? 240 : 190,
            flex: 1,
            headerAlign: 'left',
            renderHeader: leftHeader(t('dashboard.filters.savings')),
            renderCell: (params: any) =>
                renderMetricCell({
                    displayValue: formatCurrency(params.value ?? 0, currency, locale, true, 1),
                    barValue: params.value ?? 0,
                    max: maxValues.savings,
                    barColor: BAR_COLORS.savings,
                    current: params.row.savings,
                    compare: params.row.savings_compare,
                    invertedColor: false,
                    showCompare,
                    evolutionMode,
                    compareKind: 'currency',
                    currency,
                    locale,
                }),
            type: 'number',
        },
        {
            field: 'ad_cost_with_cb',
            headerName: t('dashboard.global.ad_cost'),
            minWidth: isCompare ? 230 : 180,
            flex: 1,
            headerAlign: 'left',
            renderHeader: leftHeader(t('dashboard.global.ad_cost')),
            renderCell: (params: any) =>
                renderMetricCell({
                    displayValue: formatCurrency(params.value ?? 0, currency, locale, true, 1),
                    barValue: params.value ?? 0,
                    max: maxValues.ad_cost_with_cb,
                    barColor: BAR_COLORS.ad_cost_with_cb,
                    current: params.row.ad_cost_with_cb,
                    compare: params.row.ad_cost_with_cb_compare,
                    invertedColor: true,
                    showCompare,
                    evolutionMode,
                    compareKind: 'currency',
                    currency,
                    locale,
                }),
            type: 'number',
        },
        {
            field: 'revenue_or_conversions',
            headerName: revenueLabel,
            minWidth: isCompare ? 230 : 180,
            flex: 1,
            headerAlign: 'left',
            renderHeader: leftHeader(revenueLabel),
            renderCell: (params: any) => {
                const value = params.value ?? 0;
                return renderMetricCell({
                    displayValue: isLead ? formatNumber(value) : formatCurrency(value, currency, locale, true, 1),
                    barValue: value,
                    max: maxValues.revenue_or_conversions,
                    barColor: BAR_COLORS.revenue_or_conversions,
                    current: params.row.revenue_or_conversions,
                    compare: params.row.revenue_or_conversions_compare,
                    invertedColor: false,
                    showCompare,
                    evolutionMode,
                    compareKind: isLead ? 'number' : 'currency',
                    currency,
                    locale,
                });
            },
            type: 'number',
        },
    ];
};

export default function DataGridPerformanceTable({ data, split, compare, sinceCrossBrand, tabs, tabValue, onTabChange }: any) {
    const filterStore = useSelector((state: any) => state.filters);
    const { t, currentLang } = useLocales();
    const locale = currentLang.value;
    const workspace = useCurrentWorkspace();
    const currency = workspace?.currency || 'USD';
    const theme = useTheme();
    const filteredRows = data.filter((row: any) => row.split !== null);
    const isCompare = !!filterStore?.compare;
    // %evol toggle (only meaningful when the date picker comparison is on). ON shows the
    // relative % change; OFF shows the absolute delta (pp for percentage metrics).
    const [showEvolution, setShowEvolution] = useState(true);
    const showEvolutionToggle = isCompare && split !== 'date';

    const maxValues = {
        savings: getMaxValue(filteredRows, 'savings'),
        ad_cost_with_cb: getMaxValue(filteredRows, 'ad_cost_with_cb'),
        revenue_or_conversions: getMaxValue(filteredRows, 'revenue_or_conversions'),
        blended_ctr: getMaxValue(filteredRows, 'blended_ctr'),
        brand_traffic: getMaxValue(filteredRows, 'brand_traffic')
    };

    const columns: any = getColumns({
        t,
        currentLang,
        workspace,
        theme,
        filterStore,
        split,
        isCompare,
        evolutionMode: showEvolution,
        currency,
        locale,
        maxValues,
        sinceCrossBrand
    });

    const apiRef = useGridApiRef();
    const columnsBtnRef = useRef<HTMLButtonElement>(null);
    const filterBtnRef = useRef<HTMLButtonElement>(null);
    // La ClickAwayListener du panneau se ferme sur pointerup/mouseup (avant le click).
    // On mémorise donc l'état d'ouverture au mousedown pour un vrai toggle au click.
    const columnsWasOpenRef = useRef(false);
    const filterWasOpenRef = useRef(false);
    const [panelAnchor, setPanelAnchor] = useState<HTMLElement | null>(null);

    const CustomToolbar = useMemo(() => () => (
        <GridToolbarContainer
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                pl: '20px',
                pr: '20px !important',
                py: 0,
                minHeight: 40,
                height: 40,
                borderBottom: '1px solid #eaeaea',
            }}
        >
            {tabs && tabs.length > 0 ? (
                <Tabs
                    value={tabValue ?? 0}
                    onChange={onTabChange}
                    TabIndicatorProps={{ sx: { backgroundColor: 'rgba(118, 85, 193, 1)', height: '2px' } }}
                    sx={{
                        minHeight: 38,
                        '& .MuiTab-root': {
                            minHeight: 38,
                            minWidth: 0,
                            px: 0,
                            py: 0,
                            // Le thème global (overrides/components/tabs.tsx) impose marginRight 40px ;
                            // on le ramène à 12px pour cette grille.
                            '&:not(:last-of-type)': { marginRight: '24px' },
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: 14,
                            color: '#919EAB',
                            '&.Mui-selected': { color: '#7655C1' },
                        },
                    }}
                >
                    {tabs.map((label: string) => <Tab key={label} label={label} />)}
                </Tabs>
            ) : <Box />}
            <Stack direction="row" alignItems="center" spacing={0.5}>
                {showEvolutionToggle && (
                    <Tooltip title={showEvolution ? 'Showing % evolution — click for absolute difference' : 'Showing absolute difference — click for % evolution'} placement="top">
                        <Box
                            onClick={() => setShowEvolution((v) => !v)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '2px',
                                px: '6px',
                                py: '4px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                userSelect: 'none',
                                bgcolor: showEvolution ? 'rgba(145,158,171,0.08)' : 'transparent',
                                // Le libellé "evolution" reste bleu quel que soit l'état ; l'état actif/inactif
                                // est porté par le fond (pastille) et l'icône %/# — pas par le grisé du texte.
                                color: '#1e88e5',
                                mr: 0.5,
                            }}
                        >
                            <Iconify icon={showEvolution ? 'material-symbols:percent-rounded' : 'material-symbols:tag-rounded'} width={16} sx={{ color: 'inherit' }} />
                            <Typography sx={{ fontSize: 14, fontWeight: 500, lineHeight: '16.8px', color: 'inherit', whiteSpace: 'nowrap', mx: '2px' }}>
                                evolution
                            </Typography>
                            <Iconify icon="mi:switch" width={16} sx={{ color: 'inherit' }} />
                        </Box>
                    </Tooltip>
                )}

                <Tooltip title="Show/hide columns" placement="top">
                    <IconButton
                        ref={columnsBtnRef}
                        size="small"
                        onMouseDown={() => {
                            // Capturé avant que la ClickAwayListener ne referme le panneau.
                            const panel = apiRef.current?.state?.preferencePanel;
                            columnsWasOpenRef.current = !!(panel?.open && panel?.openedPanelValue === GridPreferencePanelsValue.columns);
                        }}
                        onClick={() => {
                            // Re-clic sur l'icône => le panneau a déjà été fermé par la ClickAwayListener, on le laisse fermé (toggle).
                            if (columnsWasOpenRef.current) {
                                apiRef.current?.hidePreferences();
                                return;
                            }
                            setPanelAnchor(columnsBtnRef.current);
                            apiRef.current?.showPreferences(GridPreferencePanelsValue.columns);
                        }}
                        sx={{ width: 24, height: 24, padding: '3px', color: '#919EAB' }}
                    >
                        <Iconify icon="mingcute:calendar-week-line" width={18} />
                    </IconButton>
                </Tooltip>

                <Box sx={{ width: '1px', height: '20px', bgcolor: '#eaeaea', mx: '4px !important' }} />

                <Tooltip title="Apply filters" placement="top">
                    <IconButton
                        ref={filterBtnRef}
                        size="small"
                        onMouseDown={() => {
                            // Capturé avant que la ClickAwayListener ne referme le panneau.
                            const panel = apiRef.current?.state?.preferencePanel;
                            filterWasOpenRef.current = !!(panel?.open && panel?.openedPanelValue === GridPreferencePanelsValue.filters);
                        }}
                        onClick={() => {
                            // Re-clic sur l'icône => le panneau a déjà été fermé par la ClickAwayListener, on le laisse fermé (toggle).
                            if (filterWasOpenRef.current) {
                                apiRef.current?.hidePreferences();
                                return;
                            }
                            setPanelAnchor(filterBtnRef.current);
                            apiRef.current?.showFilterPanel();
                        }}
                        sx={{ width: 24, height: 24, padding: '3px', color: '#919EAB' }}
                    >
                        <Iconify icon="ic:round-filter-list" width={18} />
                    </IconButton>
                </Tooltip>

                <Tooltip title="Download table" placement="top">
                    <IconButton
                        size="small"
                        onClick={() => apiRef.current?.exportDataAsCsv({ fileName: 'performance_split', utf8WithBom: true })}
                        sx={{ width: 24, height: 24, padding: '3px', color: '#919EAB' }}
                    >
                        <Iconify icon="lets-icons:export" width={16} />
                    </IconButton>
                </Tooltip>
            </Stack>
        </GridToolbarContainer>
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ), [tabs, tabValue, onTabChange, showEvolutionToggle, showEvolution]);

    const [paginationModel, setPaginationModel] = useState({ pageSize: 10, page: 0 });

    const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({});

    return (
        <Box sx={{
            position: 'relative',
            '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus': {
                outline: 'none !important',
            },
            '& .MuiDataGrid-cell--pinnedLeft': {
                backgroundColor: theme.palette.mode === "dark" ? '#212b36 !important' : 'white !important',
            },
            "& .MuiDataGrid-cell": {
                overflow: 'visible !important',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                borderRight: '1px solid #eaeaea',
                borderBottom: '1px solid #eaeaea',
            },
            '& .MuiDataGrid-main': {
                overflow: 'visible !important',
            },

            '& .MuiDataGrid-virtualScroller': {
                overflow: 'visible !important',
            },
            '& .MuiDataGrid-columnHeaders': {
                position: 'relative',
                zIndex: 1,
                backgroundColor: 'rgba(226, 229, 233, 0.16)',
            },
            '& .MuiDataGrid-pinnedColumnHeaders': {
                backgroundColor: 'rgba(226, 229, 233, 0.16)',
            },
            '& .MuiDataGrid-columnHeader': {
                backgroundColor: 'transparent',
            },
            '& .MuiDataGrid-footerContainer': {
                minHeight: 40,
                height: 40,
                backgroundColor: 'rgba(226, 229, 233, 0.16)',
                borderTop: '1px solid #eaeaea',
            },
            '& .MuiDataGrid-footerContainer .MuiToolbar-root': {
                minHeight: 40,
            },
        }}>
            <DataGridPro
                autoHeight
                columnHeaderHeight={36}
                rowHeight={44}
                disableRowSelectionOnClick
                rows={filteredRows.map((r: any, i: any) => ({ id: i, ...r }))}
                pagination
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={setColumnVisibilityModel}
                paginationModel={paginationModel}
                columns={columns}
                onPaginationModelChange={setPaginationModel}
                disableDensitySelector
                apiRef={apiRef}
                slots={{ toolbar: CustomToolbar }}
                slotProps={{ panel: { anchorEl: panelAnchor } }}
                initialState={{
                    pinnedColumns: { left: ['split'] }
                }}
            />
        </Box>
    );
}

function getMaxValue(rows: any[], key: string) {
    return Math.max(...rows.map((row: any) => row[key] ?? 0));
}
